import { PanelQueue, isDeferredKind } from './panel-queue.js';
import { CallSummary, type CallSummaryData } from './components/call-summary.js';
import { BookingConfirmed, type BookingConfirmedPayload } from './components/booking-confirmed.js';
import { BriefSubmitted, type BriefSubmittedPayload } from './components/brief-submitted.js';
import { CaptureCard, type CaptureUpdate } from './components/capture-card.js';
import { ContactCard, type ShowContactPayload } from './components/contact-card.js';
import { ContactDetailsCard, type ContactDetailsConfirmation } from './components/contact-details-card.js';
import { IntakePlaybookCard } from './components/intake-playbook.js';
import type { IntakePlaybookReady, IntakePlaybookUpdated } from '@merlin/project-schema/intake-playbook';
import { ContextBadges, type ContextTag } from './components/context-badges.js';
import { ExampleBuildsOverlay, type ShowExampleBuildsPayload } from './components/example-builds-overlay.js';
import { OfferCards, type ShowOffersPayload } from './components/offer-cards.js';
import { SavingsCard, type SavingsPayload } from './components/savings-card.js';
import { ServiceDiagram, type DiagramPayload } from './components/service-diagram.js';
import { WorkflowDiagramThreeColumn } from './components/workflow-diagram.js';
import {
  parseServerJsonEvent,
  type ServerJsonEvent,
  type WorkflowDiagramPayload,
  type ContactDetailsUpdated,
  type ContactDetailsRejected,
} from './control-events.js';
import { BriefingBook, type ShowBriefingBookPayload } from './components/briefing-book.js';
import { FormCard, type CallerDetailsSubmission } from './components/form-card.js';
import { ToolPill } from './components/tool-pill.js';
import { NavStack, type PanelId, type NavEntry } from './nav-stack.js';
import { MerlinOrb } from './orb.js';
import { UI } from './ui.js';
import { BrowserRealtimeController } from './realtime-session.js';
import { GeminiUiEvents } from './gemini-ui.js';

function isInputFocused(): boolean {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
    || (el as HTMLElement).isContentEditable;
}

/**
 * Pick the variant from the page's pathname.
 *
 * Intake is opt-in via the explicit /intake path. Everything else —
 * including the bare `/` root and /sales — runs the sales (booking)
 * variant, matching the server's `resolveVariant` default.
 *
 * Vite's dev server SPA fallback serves index.html for any path, so
 * one bundle runs in both variants.
 */
function detectVariant(): 'sales' | 'intake' {
  const path = window.location.pathname;
  if (path === '/intake' || path.startsWith('/intake/')) {
    return 'intake';
  }
  return 'sales';
}

function callerTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

function clientInstanceId(): string {
  const storageKey = 'merlin.client-instance-id';
  try {
    const existing = localStorage.getItem(storageKey);
    if (existing && /^[A-Za-z0-9_-]{16,128}$/.test(existing)) return existing;
  } catch {
    // Storage can be unavailable in hardened/private browser contexts.
  }
  const suffix = typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID().replaceAll('-', '')
    : `${Date.now()}_${Math.random().toString(36).slice(2)}`;
  const created = `merlin_${suffix}`;
  try { localStorage.setItem(storageKey, created); } catch { /* non-fatal */ }
  return created;
}

function requireEl<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`Missing element: #${id}`);
  return node as T;
}

/** Aggregated state the after-call summary reads from. */
interface SessionTrace {
  startedAt?: Date;
  endedAt?: Date;
  provider?: string;
  captures: Map<string, string>;
  buildsShown: Set<string>;
  savingsShown?: number;
  bookingReference?: string;
  bookingFailed?: boolean;
  bookingEmail?: string;
  bookingStartUtc?: string;
  bookingCallerTimezone?: string;
  bookingCancelUrl?: string;
  bookingRescheduleUrl?: string;
  escalated: boolean;
  /**
   * Set once submit_brief succeeds (intake variant). When true, the
   * brief-submitted card (with its PDF download) is the call's terminal
   * surface and maybeShowSummary() must NOT replace it with the generic
   * call summary — doing so buried the card + PDF when the WS closed
   * after end_call.
   */
  briefSubmitted?: boolean;
}

function newTrace(): SessionTrace {
  return {
    captures: new Map(),
    buildsShown: new Set(),
    escalated: false,
  };
}

export class SerializedStop<T extends object> {
  #task?: Promise<void>;
  #controller?: T;

  owns(controller: T): boolean {
    return this.#task !== undefined && this.#controller === controller;
  }

  run(
    controller: T | undefined,
    close: (controller: T | undefined) => Promise<void>,
    finalize: (controller: T | undefined) => Promise<void>,
  ): Promise<void> {
    if (this.#task) return this.#task;
    this.#controller = controller;
    const run = (async () => {
      try {
        await close(controller);
      } finally {
        await finalize(controller);
      }
    })();
    const task = run.finally(() => {
      if (this.#task !== task) return;
      this.#task = undefined;
      this.#controller = undefined;
    });
    this.#task = task;
    return task;
  }
}

class App {
  private readonly ui = new UI();
  private readonly realtimeAudio = document.createElement('audio');
  private readonly orb: MerlinOrb;
  private readonly captureCard: CaptureCard;
  private readonly offerCards: OfferCards;
  private readonly toolPill: ToolPill;
  private readonly bookingCard: BookingConfirmed;
  private readonly briefCard: BriefSubmitted;
  private readonly contextBadges: ContextBadges;
  private readonly exampleBuildsOverlay: ExampleBuildsOverlay;
  private readonly contactCard: ContactCard;
  private readonly savingsCard: SavingsCard;
  private readonly serviceDiagram: ServiceDiagram;
  private readonly workflowDiagram: WorkflowDiagramThreeColumn;
  private readonly callSummary: CallSummary;
  private readonly navStack: NavStack;
  private readonly briefingBook: BriefingBook;
  private readonly formCard: FormCard;
  private readonly contactDetailsCard: ContactDetailsCard;
  private readonly contactDetailsRoot: HTMLElement;
  private readonly intakePlaybook: IntakePlaybookCard;
  private readonly playbookRoot: HTMLElement;
  /** Intake-only right-hand rail (notebook, contact, docked diagram, playbook). Absent for Sales. */
  private readonly intakeRail?: HTMLElement;
  private readonly intakeWorkflowRoot?: HTMLElement;
  private readonly intakeWorkflowDiagram?: WorkflowDiagramThreeColumn;
  /** Mirrors .stage-grid[data-rail]; true once any rail panel is visible. Intake-only. */
  private railOpen = false;
  private readonly panelQueue = new PanelQueue();
  private readonly stops = new SerializedStop<BrowserRealtimeController>();
  private realtime?: BrowserRealtimeController;
  private trace: SessionTrace = newTrace();
  private pendingCallerDetails?: CallerDetailsSubmission;
  private identityAckTimer?: number;
  private pendingContactDetails?: ContactDetailsConfirmation;
  private contactAckTimer?: number;
  private summaryTimer?: number;
  private sessionGeneration = 0;
  /** Gemini Intake only; reset whenever the call ends. */
  private micMuted = false;
  /**
   * Gemini only. Shows a subtle "waking up" status if the greeting is taking
   * a while — cleared the moment any real activity (speaking/thinking/audio)
   * arrives, or when the call ends/fails. Never overrides an error state.
   */
  private greetingWaitTimer?: number;

  constructor() {
    const canvas = document.getElementById('orb') as HTMLCanvasElement | null;
    if (!canvas) throw new Error('orb canvas missing');
    this.orb = new MerlinOrb(canvas);
    this.orb.start();
    this.orb.setState('idle');
    this.realtimeAudio.autoplay = true;
    this.realtimeAudio.hidden = true;
    this.realtimeAudio.setAttribute('playsinline', '');
    document.body.append(this.realtimeAudio);

    this.captureCard = new CaptureCard(requireEl('capture'));
    this.offerCards = new OfferCards(requireEl('offers'));
    this.toolPill = new ToolPill(requireEl('tool-pills'));
    this.bookingCard = new BookingConfirmed(requireEl('booking-confirmed'));
    this.briefCard = new BriefSubmitted(requireEl('brief-submitted'));
    this.contextBadges = new ContextBadges(requireEl('context-badges'));
    this.exampleBuildsOverlay = new ExampleBuildsOverlay(requireEl('example-builds'));
    this.contactCard = new ContactCard(requireEl('contact'));
    this.savingsCard = new SavingsCard(requireEl('savings'));
    this.serviceDiagram = new ServiceDiagram(requireEl('diagram'));
    this.workflowDiagram = new WorkflowDiagramThreeColumn(requireEl('workflow-diagram'));
    this.callSummary = new CallSummary(requireEl('summary'));
    this.navStack = new NavStack({
      showPanel: (entry) => this.showPanelFromEntry(entry),
      hidePanel: (panelId) => this.hidePanelById(panelId),
      onDepthChange: (depth) => this.updateBackButtons(depth),
    });
    this.briefingBook = new BriefingBook(requireEl('briefing-book'));
    this.formCard = new FormCard(requireEl('caller-form'));

    // Intake gets a dedicated right-hand rail holding the notebook, live
    // contact card, docked workflow diagram and playbook, in that order.
    // Sales keeps its existing DOM untouched — no rail is created, and its
    // overlays stay body-level.
    let intakeRail: HTMLElement | undefined;
    if (detectVariant() === 'intake') {
      document.body.dataset.variant = 'intake';
      const stageGrid = requireEl('capture').parentElement!;
      intakeRail = document.createElement('div');
      intakeRail.id = 'intake-rail';
      intakeRail.className = 'intake-rail';
      intakeRail.setAttribute('aria-label', "Merlin's workspace");
      stageGrid.append(intakeRail);
      intakeRail.append(requireEl('capture'));
    }
    this.intakeRail = intakeRail;
    const extrasParent = intakeRail ?? requireEl('capture').parentElement!;

    this.contactDetailsRoot = document.createElement('aside');
    this.contactDetailsRoot.id = 'intake-contact-details';
    extrasParent.append(this.contactDetailsRoot);
    this.contactDetailsCard = new ContactDetailsCard(this.contactDetailsRoot,
      detail => this.submitContactDetails(detail), revision => this.editContactDetails(revision));

    if (intakeRail) {
      this.intakeWorkflowRoot = document.createElement('section');
      this.intakeWorkflowRoot.id = 'intake-workflow';
      intakeRail.append(this.intakeWorkflowRoot);
      this.intakeWorkflowDiagram = new WorkflowDiagramThreeColumn(this.intakeWorkflowRoot, { docked: true });
    }

    this.playbookRoot = document.createElement('aside');
    this.playbookRoot.id = 'intake-playbook';
    extrasParent.append(this.playbookRoot);
    this.intakePlaybook = new IntakePlaybookCard(this.playbookRoot, () => {
      const wasHidden = this.contactDetailsRoot.hidden;
      this.contactDetailsCard.open();
      this.revealIfNewlyShown(this.contactDetailsRoot, wasHidden);
      this.intakePlaybook.setContactRevision(this.contactDetailsCard.revision);
    });
    this.panelQueue.setFlushHandler((e) => this.dispatchVisual(e.kind, e.payload));

    // Keep .stage-grid[data-rail] in sync with whatever the rail's own
    // panels do to their own hidden/visible state, wherever that happens
    // (contact card apply/clear, capture reset/apply, workflow show/hide,
    // playbook update/clear). A MutationObserver on the rail subtree is the
    // single, robust source of truth instead of threading a call through
    // every one of those call sites.
    if (intakeRail) {
      new MutationObserver(() => this.syncRailState())
        .observe(intakeRail, { attributes: true, attributeFilter: ['hidden', 'class'], subtree: true });
    }
  }

  init() {
    this.ui.setStatus('idle');
    this.ui.setCallActive(false);
    this.ui.onMicClick(() => this.start());
    this.ui.onMuteClick(() => this.toggleMute());
    this.ui.onEndClick(() => {
      void this.stop('ended by user');
    });

    // ESC closes the topmost visible layer. Priority: NavStack panels first,
    // then drawer-style panels (briefing-book, transcript). Backspace / Alt+Left
    // navigates back one entry within a NavStack panel.
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        if (this.navStack.depth > 0) {
          e.preventDefault();
          e.stopPropagation();
          if (this.navStack.topPanelId !== 'caller-details-form') {
            this.navStack.closeAll();
          }
          return;
        }
        if (this.briefingBook.isOpen) {
          this.briefingBook.hide();
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        if (this.ui.isDrawerOpen) {
          this.ui.closeDrawer();
          e.preventDefault();
          e.stopPropagation();
          return;
        }
      }
      const isBack =
        (e.key === 'Backspace' && !isInputFocused()) ||
        (e.key === 'ArrowLeft' && e.altKey);
      if (isBack && this.navStack.canGoBack && this.navStack.topPanelId !== 'caller-details-form') {
        e.preventDefault();
        this.navStack.back();
      }
    });

    // Panel nav-back and nav-close custom events from panel components.
    document.addEventListener('merlin:nav-back', () => {
      if (this.navStack.topPanelId !== 'caller-details-form') this.navStack.back();
    });
    document.addEventListener('merlin:nav-close', () => {
      if (this.navStack.topPanelId !== 'caller-details-form') this.navStack.closeAll();
    });

    // Click-to-diagram from the example builds overlay.
    document.addEventListener('merlin:show-diagram', (e) => {
      const diagram = (e as CustomEvent<DiagramPayload>).detail;
      this.navStack.push('diagram', diagram);
      this.trace.buildsShown.add(diagram.title);
    });

    // Caller-details form submission → forward to server.
    document.addEventListener('merlin:caller-details-submitted', (e) => {
      const detail = (e as CustomEvent<CallerDetailsSubmission>).detail;
      try {
        if (!this.realtime) throw new Error('realtime controller unavailable');
        this.realtime.sendControlMessage({ type: 'caller_details_provided', data: detail });
      } catch {
        const message = 'The call is still connecting. Please submit the form again.';
        this.formCard.reject(message);
        this.ui.showBanner(message, 'error');
        return;
      }
      this.pendingCallerDetails = detail;
      if (this.identityAckTimer) window.clearTimeout(this.identityAckTimer);
      this.identityAckTimer = window.setTimeout(() => {
        if (!this.pendingCallerDetails) return;
        this.pendingCallerDetails = undefined;
        const message = 'Merlin did not receive those details. Please try again.';
        this.formCard.reject(message);
        this.ui.showBanner(message, 'error');
      }, 8_000);
    });
  }

  private showPanelFromEntry(entry: NavEntry): void {
    switch (entry.panelId) {
      case 'offers': this.offerCards.show(entry.payload as ShowOffersPayload); break;
      case 'example-builds': this.exampleBuildsOverlay.show(entry.payload as ShowExampleBuildsPayload); break;
      case 'contact': this.contactCard.show(entry.payload as ShowContactPayload); break;
      case 'savings': this.savingsCard.show(entry.payload as SavingsPayload); break;
      case 'diagram': this.serviceDiagram.show(entry.payload as DiagramPayload); break;
      case 'workflow-diagram': this.workflowDiagram.show(entry.payload as WorkflowDiagramPayload); break;
      case 'summary': this.callSummary.show(entry.payload as CallSummaryData); break;
      case 'booking-confirmed': this.bookingCard.show(entry.payload as BookingConfirmedPayload); break;
      case 'brief-submitted': this.briefCard.show(entry.payload as BriefSubmittedPayload); break;
      case 'caller-details-form': this.formCard.show(); break;
    }
  }

  private hidePanelById(panelId: PanelId): void {
    switch (panelId) {
      case 'offers': this.offerCards.hide(); break;
      case 'example-builds': this.exampleBuildsOverlay.hide(); break;
      case 'contact': this.contactCard.hide(); break;
      case 'savings': this.savingsCard.hide(); break;
      case 'diagram': this.serviceDiagram.hide(); break;
      case 'workflow-diagram': this.workflowDiagram.hide(); break;
      case 'summary': this.callSummary.hide(); break;
      case 'booking-confirmed': this.bookingCard.hide(); break;
      case 'brief-submitted': this.briefCard.hide(); break;
      case 'caller-details-form': this.formCard.hide(); break;
    }
  }

  private updateBackButtons(depth: number): void {
    const btn = document.querySelector<HTMLElement>('.visible [data-action="back"]');
    btn?.toggleAttribute('hidden', depth <= 1);
  }

  private submitContactDetails(detail: ContactDetailsConfirmation): void {
    this.clearContactSubmission();
    try {
      if (detectVariant() !== 'intake' || this.realtime?.provider !== 'gemini') throw new Error('contact confirmation unavailable');
      this.pendingContactDetails = detail;
      this.realtime.sendControlMessage({ type: 'caller_details_provided', ...detail });
    } catch {
      this.clearContactSubmission();
      this.contactDetailsCard.reject('Your confirmation could not be sent. Check that the call is connected and try again.');
      return;
    }
    this.contactAckTimer = window.setTimeout(() => {
      if (!this.pendingContactDetails) return;
      const revision = this.pendingContactDetails.revision;
      this.clearContactSubmission();
      this.intakePlaybook.invalidateContact();
      try {
        if (this.realtime?.provider !== 'gemini') throw new Error('contact editing unavailable');
        this.realtime.sendControlMessage({ type: 'contact_details_editing', revision });
      } catch {
        // Best-effort revoke: the reject below still tells the caller to retry.
      }
      this.contactDetailsCard.reject('Your confirmation has not been acknowledged. Please check the details and try again.');
    }, 8_000);
  }

  private editContactDetails(revision: number | undefined): void {
    this.clearContactSubmission();
    this.intakePlaybook.invalidateContact();
    if (revision === undefined) return;
    try {
      if (this.realtime?.provider !== 'gemini') throw new Error('contact editing unavailable');
      this.realtime.sendControlMessage({ type: 'contact_details_editing', revision });
    } catch {
      // Without server revocation, continuing could use the old handoff consent.
      this.fail('Your updated details could not reach Merlin. Please start a new call.');
    }
  }

  /** Gemini Intake only. Throws away all mic input by ending the audio turn cleanly. */
  private toggleMute(): void {
    if (!this.realtime || this.realtime.provider !== 'gemini') return;
    const next = !this.micMuted;
    try {
      this.realtime.setMicrophoneMuted(next);
    } catch {
      return;
    }
    this.micMuted = next;
    this.ui.setMuted(next);
  }

  /**
   * Scrolls the intake rail to reveal `el` (never scrollIntoView, never moves
   * focus). Skipped entirely while the caller has focus inside the rail
   * (e.g. mid-typing in a contact field) so a new panel never yanks them
   * away. When the rail itself doesn't scroll internally — no overflow, or
   * the ≤900px layout where the rail flows in the page instead — the page
   * is scrolled to the panel instead, respecting reduced-motion.
   */
  private revealRailPanel(el: HTMLElement): void {
    const rail = this.intakeRail;
    if (!rail) return;
    const active = document.activeElement;
    if (active instanceof Node && rail.contains(active)) return;
    if (rail.scrollHeight > rail.clientHeight && !window.matchMedia?.('(max-width: 900px)').matches) {
      rail.scrollTop = el.offsetTop;
      return;
    }
    const rect = el.getBoundingClientRect();
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    window.scrollTo({ top: rect.top + window.scrollY - 16, behavior: reducedMotion ? 'auto' : 'smooth' });
  }

  private revealIfNewlyShown(el: HTMLElement, wasHidden: boolean): void {
    if (wasHidden && !el.hidden) this.revealRailPanel(el);
  }

  /** #capture only shows/hides via the `visible` class; every other rail child uses `hidden`. */
  private isRailPanelVisible(el: Element): boolean {
    if (!(el instanceof HTMLElement)) return false;
    return el.id === 'capture' ? el.classList.contains('visible') : !el.hidden;
  }

  /**
   * Recomputes whether any rail panel is visible and flips
   * .stage-grid[data-rail="open"] (mirrored onto body[data-rail] — see
   * below) on the real, observed edge only (never on every mutation) —
   * Intake starts and returns to the centred single-column layout, matching
   * Sales, until a panel appears. On the edge, the orb's horizontal move is
   * animated with a short FLIP transform (measure before, let the
   * CSS/attribute change move it, then ease from the old position) so the
   * shift reads as a slide rather than a jump; skipped entirely under
   * prefers-reduced-motion, where the change is instant.
   */
  private syncRailState(): void {
    const rail = this.intakeRail;
    const stageGrid = rail?.parentElement;
    if (!rail || !stageGrid) return;
    const open = [...rail.children].some((child) => this.isRailPanelVisible(child));
    if (open === this.railOpen) return;
    this.railOpen = open;

    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    const orbWrap = reducedMotion ? null : document.querySelector<HTMLElement>('.orb-wrap');
    const before = orbWrap?.getBoundingClientRect();

    // Mirrored onto body too: .app/.stage's definite-height lock (CSS) is an
    // ANCESTOR of .stage-grid, so it can only be gated off a flag body also
    // carries — closed Intake must drop that lock, not just the column
    // split, or a Sales-sized orb can outgrow the locked row's real leftover
    // space at shorter desktop heights (it may on Sales too, but Sales has
    // no lock, so it grows/scrolls there instead of overflowing in place).
    if (open) { stageGrid.setAttribute('data-rail', 'open'); document.body.setAttribute('data-rail', 'open'); }
    else { stageGrid.removeAttribute('data-rail'); document.body.removeAttribute('data-rail'); }

    if (!orbWrap || !before) return;
    const after = orbWrap.getBoundingClientRect();
    const deltaX = before.left - after.left;
    if (!deltaX) return;
    orbWrap.style.transition = 'none';
    orbWrap.style.transform = `translateX(${deltaX}px)`;
    void orbWrap.offsetWidth; // force a reflow so the line above paints before the one below eases out
    orbWrap.style.transition = 'transform 320ms ease';
    orbWrap.style.transform = '';
    orbWrap.addEventListener('transitionend', () => { orbWrap.style.transition = ''; }, { once: true });
  }

  private clearContactSubmission(): void {
    if (this.contactAckTimer) window.clearTimeout(this.contactAckTimer);
    this.contactAckTimer = undefined;
    this.pendingContactDetails = undefined;
  }

  private async start() {
    this.sessionGeneration += 1;
    if (this.summaryTimer) window.clearTimeout(this.summaryTimer);
    this.summaryTimer = undefined;
    this.ui.hideBanner();
    this.ui.resetCaption();
    this.captureCard.reset();
    this.briefCard.clear();
    this.offerCards.hide();
    this.exampleBuildsOverlay.hide();
    this.contactCard.hide();
    this.savingsCard.hide();
    this.serviceDiagram.hide();
    this.callSummary.hide();
    this.briefingBook.hide();
    this.bookingCard.hide();
    this.formCard.hide();
    this.clearIdentitySubmission();
    this.clearContactSubmission();
    this.contactDetailsCard.clear();
    this.intakePlaybook.clear();
    this.intakeWorkflowDiagram?.clear();
    this.micMuted = false;
    this.ui.setMuted(false);
    this.ui.setMuteAvailable(false);
    this.navStack.clear();
    this.panelQueue.clear();
    this.contextBadges.clear();
    this.toolPill.clear();
    this.trace = newTrace();
    this.trace.startedAt = new Date();
    this.ui.setStatus('connecting');
    this.ui.setCaptionIntro('Click “Allow” if your browser asks for microphone access…');
    this.ui.setCallActive(true);
    this.orb.setState('thinking');

    const geminiUi = new GeminiUiEvents({
      pushTranscript: (speaker, text, finished) => this.ui.pushTranscript(speaker, text, finished),
      commitTurn: (speaker) => this.ui.commitTurn(speaker),
      flushPanels: () => this.panelQueue.flush(),
      setActivity: (state) => {
        // Any real activity — speaking, thinking (a tool round trip), or a
        // return to listening — is forward progress on the greeting: stop
        // showing "waking up" for it.
        this.clearGreetingWait();
        this.ui.setStatus(state === 'speaking' ? 'speaking' : 'live');
        this.orb.setState(state);
      },
    });
    const controller = new BrowserRealtimeController({
      variant: detectVariant(),
      timezone: callerTimezone(),
      clientInstanceId: clientInstanceId(),
      audioElement: this.realtimeAudio,
      realtimeEvents: {
        onSemanticEvent: (event) => {
          if (this.realtime !== controller) return;
          geminiUi.handle(event);
        },
        onConnectionChange: (status) => {
          if (this.realtime !== controller) return;
          if (status === 'connected') {
            if (controller.provider === 'gemini') geminiUi.restoreActivity();
            return;
          }
          if (status === 'connecting') this.ui.setStatus('connecting');
        },
        onTranscriptDelta: (event) => {
          if (this.realtime !== controller) return;
          this.ui.pushTranscript('merlin', event.delta, false);
        },
        onRawEvent: (event) => {
          if (this.realtime !== controller) return;
          const type = (event as { type?: string }).type;
          if (type === 'conversation.item.input_audio_transcription.completed') {
            const transcript = (event as { transcript?: unknown }).transcript;
            if (typeof transcript === 'string' && transcript.trim()) {
              this.ui.pushTranscript('user', transcript, true);
            }
          } else if (type === 'response.output_audio_transcript.done') {
            this.ui.pushTranscript('merlin', '', true);
          }
          if (type === 'response.output_audio.delta' || type === 'response.output_audio.started') {
            this.ui.setStatus('speaking');
            this.orb.setState('speaking');
          } else if (type === 'response.output_audio.done' || type === 'response.done') {
            this.ui.setStatus('live');
            this.orb.setState('listening');
          } else if (type === 'input_audio_buffer.speech_started') {
            this.orb.setState('listening');
          }
        },
        onControlMessage: (message) => {
          if (this.realtime !== controller || this.stops.owns(controller)) return;
          const event = parseServerJsonEvent(message);
          if (event) {
            void this.handleJson(event);
          } else {
            console.warn('[merlin] ignored an invalid server control message');
          }
        },
        onControlClose: () => {
          if (this.realtime !== controller) return;
          if (this.stops.owns(controller)) return;
          this.clearGreetingWait();
          this.realtime = undefined;
          this.micMuted = false;
          this.ui.setMuted(false);
          this.ui.setMuteAvailable(false);
          this.finaliseTrace();
          void this.teardown();
          this.ui.setStatus('idle', 'Ready');
          this.ui.setCallActive(false);
          this.orb.setState('idle');
          this.maybeShowSummary();
        },
      },
    });
    this.realtime = controller;

    try {
      await controller.connect();
      if (this.realtime !== controller) return;
      this.trace.provider = controller.provider;
      this.ui.setProvider(controller.provider);
      this.ui.setStatus('live');
      this.ui.setCaptionIntro('Connected. Merlin is ready.');
      this.orb.setState('listening');
      if (detectVariant() === 'intake' && controller.provider === 'gemini') {
        this.ui.setMuteAvailable(true);
        // Session readiness precedes the greeting itself — show a subtle
        // "waking up" hint if nothing has happened for a few seconds, cleared
        // the moment any real Gemini activity arrives (see setActivity above).
        this.greetingWaitTimer = window.setTimeout(() => {
          if (this.realtime === controller) this.ui.setStatus('live', 'Merlin is waking up…');
        }, 5_000);
      }
    } catch (error) {
      if (this.realtime !== controller) return;
      this.fail(`Realtime connection failed: ${(error as Error).message}`);
    }
  }

  private async handleJson(event: ServerJsonEvent) {
    switch (event.type) {
      case 'speech_started':
        this.orb.setState('listening');
        break;
      case 'interrupted':
        this.panelQueue.flush();
        this.ui.commitTurn('merlin');
        this.orb.setState('listening');
        break;
      case 'turn_complete':
        this.panelQueue.flush();
        this.ui.commitTurn('merlin');
        this.ui.commitTurn('user');
        break;
      case 'identity_accepted': {
        if (detectVariant() === 'intake') break;
        const detail = this.pendingCallerDetails;
        if (!detail) break;
        this.clearIdentitySubmission();
        this.trace.captures.set('name', detail.name);
        this.trace.captures.set('organisation', detail.organisation);
        this.trace.captures.set('email', detail.email);
        this.formCard.accept();
        if (this.navStack.topPanelId === 'caller-details-form') this.navStack.closeAll();
        break;
      }
      case 'event':
        this.handleServerEvent(event.kind, event.payload);
        break;
      case 'error':
        this.fail(event.message);
        break;
    }
  }

  private handleServerEvent(kind: string, payload: unknown) {
    if (isDeferredKind(kind)) {
      this.panelQueue.enqueue({ kind, payload });
      return;
    }
    this.dispatchVisual(kind, payload);
  }

  private dispatchVisual(kind: string, payload: unknown) {
    switch (kind) {
      case 'contact_details_updated': {
        if (detectVariant() !== 'intake') break;
        const update = payload as ContactDetailsUpdated;
        if (update.revision > this.contactDetailsCard.revision) {
          this.clearContactSubmission();
          this.intakePlaybook.setContactRevision(update.revision);
        }
        const wasHidden = this.contactDetailsRoot.hidden;
        this.contactDetailsCard.apply(update);
        this.revealIfNewlyShown(this.contactDetailsRoot, wasHidden);
        break;
      }
      case 'contact_details_confirmed': {
        if (detectVariant() !== 'intake') break;
        const { revision } = payload as { revision: number };
        if (this.pendingContactDetails?.revision !== revision || !this.contactDetailsCard.accept(revision)) break;
        this.clearContactSubmission();
        this.intakePlaybook.confirmContact(revision);
        break;
      }
      case 'playbook_updated':
        if (detectVariant() === 'intake') {
          const wasHidden = this.playbookRoot.hidden;
          this.intakePlaybook.update(payload as IntakePlaybookUpdated);
          this.revealIfNewlyShown(this.playbookRoot, wasHidden);
        }
        break;
      case 'playbook_ready':
        if (detectVariant() === 'intake') this.intakePlaybook.ready(payload as IntakePlaybookReady);
        break;
      case 'contact_details_rejected': {
        if (detectVariant() !== 'intake') break;
        const rejection = payload as ContactDetailsRejected;
        if (rejection.revision < this.contactDetailsCard.revision) break;
        this.clearContactSubmission();
        this.intakePlaybook.invalidateContact();
        this.contactDetailsCard.reject(rejection.code === 'stale_revision'
          ? 'The details changed while you were confirming. Please check them and confirm again.'
          : 'Please check your full name, email and business or project name, and any optional phone number, then confirm again.');
        break;
      }
      case 'capture': {
        const data = payload as CaptureUpdate;
        this.captureCard.apply(data);
        if (data.status === 'confirmed') {
          this.trace.captures.set(data.slot, data.value);
        }
        break;
      }
      case 'show_offers':
        this.navStack.push('offers', payload as ShowOffersPayload);
        break;
      case 'tool_start': {
        const data = payload as { id: string; name: string };
        this.toolPill.start(data.id, data.name);
        break;
      }
      case 'tool_done': {
        const data = payload as { id: string; name: string; ok: boolean };
        this.toolPill.done(data.id, data.ok);
        break;
      }
      case 'booking': {
        const booking = payload as BookingConfirmedPayload;
        this.trace.bookingReference = booking.reference;
        this.trace.bookingFailed = Boolean(booking.failed);
        this.trace.bookingEmail = booking.email;
        this.trace.bookingStartUtc = booking.bookingStartUtc;
        this.trace.bookingCallerTimezone = booking.callerTimezone;
        this.trace.bookingCancelUrl = booking.cancelUrl;
        this.trace.bookingRescheduleUrl = booking.rescheduleUrl;
        if (booking.failed) {
          this.ui.showBanner('Booking pending — Cal.com was briefly unreachable. Team will email you a link.', 'error');
        } else {
          this.ui.showBanner('Booking confirmed — confirmation in your inbox.');
        }
        this.navStack.push('booking-confirmed', booking);
        break;
      }
      case 'escalation': {
        const { emailDelivery: delivery, kind: handoffKind } = payload as { emailDelivery?: string; kind?: string };
        this.trace.escalated = delivery === undefined || delivery === 'accepted';
        if (handoffKind === 'follow_up') {
          this.ui.showBanner(delivery === 'accepted'
            ? 'Your follow-up request was accepted for delivery to the team.'
            : delivery === 'not_sent'
              ? 'Your follow-up email was not sent. Please contact the team directly.'
              : 'Your follow-up email is unconfirmed. Please contact the team directly.');
          break;
        }
        this.ui.showBanner(delivery === 'unknown'
          ? 'Escalation email is unconfirmed. Please contact the team directly.'
          : delivery === 'not_sent'
            ? 'Escalation email was not sent. Please contact the team directly.'
            : delivery === 'accepted'
              ? 'Your escalation was accepted for delivery to the team.'
              : 'Your escalation has been recorded for the team.');
        break;
      }
      case 'info-email':
        this.ui.showBanner('Info pack queued for delivery.');
        break;
      case 'context_tag': {
        const tag = payload as ContextTag;
        this.contextBadges.add(tag);
        break;
      }
      case 'show_example_builds': {
        const data = payload as ShowExampleBuildsPayload;
        this.navStack.push('example-builds', data);
        for (const item of data.items) this.trace.buildsShown.add(item.name);
        break;
      }
      case 'show_contact':
        this.navStack.push('contact', payload as ShowContactPayload);
        break;
      case 'show_savings': {
        const data = payload as SavingsPayload;
        this.navStack.push('savings', data);
        this.trace.savingsShown = data.annualSavings;
        break;
      }
      case 'workflow_diagram': {
        const data = payload as WorkflowDiagramPayload;
        this.trace.buildsShown.add(data.title);
        if (detectVariant() === 'intake' && this.intakeWorkflowDiagram && this.intakeWorkflowRoot) {
          // Intake docks the diagram in the rail instead of the Sales
          // full-screen overlay, and it never enters the NavStack: it has
          // no Back button and Backspace/Alt+Left/Escape don't touch it.
          const wasHidden = this.intakeWorkflowRoot.hidden;
          this.intakeWorkflowDiagram.show(data);
          this.revealIfNewlyShown(this.intakeWorkflowRoot, wasHidden);
        } else {
          this.navStack.push('workflow-diagram', data);
        }
        break;
      }
      case 'show_briefing_book':
        this.briefingBook.show(payload as ShowBriefingBookPayload);
        break;
      case 'request_caller_details':
        this.navStack.push('caller-details-form', null);
        break;
      case 'caller_details_form_reprompt':
        this.formCard.flashReprompt();
        break;
      case 'brief_submitted': {
        const brief = payload as BriefSubmittedPayload;
        // Mark the brief as the terminal surface so maybeShowSummary() (which
        // fires when end_call closes the WS ~4s later) does not closeAll() and
        // bury this card + its PDF download under the generic call summary.
        this.trace.briefSubmitted = true;
        // Treat the discovery brief like a successful booking: show the
        // thanks card via NavStack so ESC + back still work, and surface
        // a banner in the corner for redundancy.
        this.ui.showBanner('Discovery brief ready — download your copy and check email status below.');
        // Clear any open panels first so the thanks card is the only
        // thing on screen at the call's terminal moment.
        this.navStack.closeAll();
        this.navStack.push('brief-submitted', brief);
        break;
      }
    }
  }

  private fail(message: string) {
    this.ui.setStatus('error', message);
    this.ui.showBanner(message, 'error');
    this.orb.setState('idle');
    void this.stop(message, true);
  }

  private stop(reason: string, failed = false): Promise<void> {
    const realtime = this.realtime;
    this.clearGreetingWait();
    this.micMuted = false;
    this.ui.setMuted(false);
    this.ui.setMuteAvailable(false);
    this.clearContactSubmission();
    this.contactDetailsCard.clear();
    this.intakePlaybook.clear();
    return this.stops.run(
      realtime,
      async (controller) => {
        if (controller) await controller.close(reason).catch(() => undefined);
      },
      async (controller) => {
        if (controller && this.realtime !== controller) return;
        if (this.realtime === controller) this.realtime = undefined;
        this.finaliseTrace();
        await this.teardown();
        if (!failed) this.ui.setStatus('idle');
        this.ui.setCallActive(false);
        this.ui.clearLatency();
        this.orb.setState('idle');
        this.maybeShowSummary();
      },
    );
  }

  private async teardown() {
    this.panelQueue.clear();
    this.clearIdentitySubmission();
    this.clearContactSubmission();
    this.contactDetailsCard.clear();
    this.intakePlaybook.clear();
    this.micMuted = false;
    this.ui.setMuted(false);
    this.ui.setMuteAvailable(false);
    if (this.navStack.topPanelId === 'caller-details-form') this.navStack.closeAll();
    if (detectVariant() === 'intake') {
      this.trace = newTrace();
      this.captureCard.reset();
      this.contextBadges.clear();
      this.ui.resetCaption();
      this.formCard.clear();
      this.workflowDiagram.clear();
      this.intakeWorkflowDiagram?.clear();
      if (this.navStack.topPanelId !== 'brief-submitted') this.navStack.closeAll();
    }
    this.orb.setAnalyser(undefined);
    this.toolPill.clear();
  }

  private clearIdentitySubmission(): void {
    if (this.identityAckTimer) window.clearTimeout(this.identityAckTimer);
    this.identityAckTimer = undefined;
    this.pendingCallerDetails = undefined;
  }

  private clearGreetingWait(): void {
    if (this.greetingWaitTimer) window.clearTimeout(this.greetingWaitTimer);
    this.greetingWaitTimer = undefined;
  }

  private finaliseTrace() {
    if (!this.trace.endedAt) this.trace.endedAt = new Date();
  }

  private maybeShowSummary() {
    const t = this.trace;
    // The brief-submitted card owns the terminal moment for intake calls and
    // carries the PDF download the caller still needs after the WS closes.
    // Never replace it with the generic call summary.
    if (t.briefSubmitted) return;
    const substantive =
      !!t.bookingReference ||
      t.escalated ||
      t.captures.size > 0 ||
      t.buildsShown.size > 0 ||
      this.contextBadges.list().length > 0 ||
      t.savingsShown != null;
    if (!substantive) return;

    const data: CallSummaryData = {
      startedAt: t.startedAt,
      endedAt: t.endedAt,
      context: this.contextBadges.list(),
      captures: Array.from(t.captures.entries()).map(([slot, value]) => ({
        slot,
        value,
      })),
      bookingReference: t.bookingReference,
      bookingFailed: t.bookingFailed,
      bookingEmail: t.bookingEmail,
      bookingStartUtc: t.bookingStartUtc,
      bookingCallerTimezone: t.bookingCallerTimezone,
      bookingCancelUrl: t.bookingCancelUrl,
      bookingRescheduleUrl: t.bookingRescheduleUrl,
      servicesShown: Array.from(t.buildsShown),
      savingsShown: t.savingsShown,
      provider: t.provider,
      escalated: t.escalated,
    };
    // Small delay so the closing animations on other overlays finish first.
    const generation = this.sessionGeneration;
    if (this.summaryTimer) window.clearTimeout(this.summaryTimer);
    this.summaryTimer = window.setTimeout(() => {
      this.summaryTimer = undefined;
      if (generation !== this.sessionGeneration) return;
      this.navStack.closeAll();
      this.navStack.push('summary', data);
    }, 360);
  }
}

if (typeof document !== 'undefined') {
  const app = new App();
  app.init();
}
